import type { User } from "features/user";
import { getEntitlementValue, hasEntitlement } from "./has-entitlement";

export const entitlementsMappings = {
  accounts: "accounts",
  "meta-ads": "meta-ads",
  "meta-ads-ai": "meta-ads-ai",
  "meta-ads-campaign": "meta-ads-campaign",
  "reports-full": "reports-full",
  "reports-limited": "reports-limited",
  "listening-keyword": "listening-keyword",
  "listening-competitors": "listening-competitors",
  "listening-sentiment": "listening-sentiment",
  "listening-audience": "listening-audience",
  "editor-library": "editor-library",
  "post-status-version": "post-status-version",
  "external-approvals": "external-approvals",
  "knock-notifications": "knock-notifications",
  "social-set-only": "social-set-only",
  "support-book-call": "support-book-call",
  "maximum-bulk-posts": "maximum-bulk-posts",
  "user-permissions": "user-permissions",
};

export type UserPermissionsTier = "basic" | "advanced";

export type UserWithEntitlements = Pick<User, "preferences"> | null | undefined;

/**
 * Parses an entitlement string as a positive integer.
 * Returns `null` for any input that isn't a strictly-positive integer:
 * - `null` / `undefined` / empty string → `null`
 * - non-numeric strings (`"abc"`, `"true"`, `"false"`) → `null`
 * - `"0"` and negative numbers → `null` (not a positive limit)
 *
 * Consumers should treat `null` as "no limit configured" and apply their
 * own default. Server-side enforcement is the source of truth for actual
 * gating.
 */
const POSITIVE_INT_PATTERN = /^[1-9]\d*$/;

export const parsePositiveIntEntitlement = (
  value: string | null | undefined
): number | null => {
  if (!value) {
    return null;
  }

  // Strict parse: the entitlement must be a pure positive-integer string.
  // Without the regex, `Number.parseInt("10abc", 10)` would silently
  // accept "10abc" as 10 and surface malformed Chargebee values as valid
  // limits. Entitlement values come from Chargebee's feature API where a
  // garbage-in value signals a config error — fail safe by returning null.
  const trimmed = value.trim();
  if (!POSITIVE_INT_PATTERN.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

class EntitlementsPermission {
  // SKE-3599: post-rebrand (2026-04-28) plan-ID → post-status-version map.
  // sked-essentials = Grow (basic), sked-professional = Accelerate (advanced).
  // The pre-rebrand display names ("Launch" → sked-essentials, "Grow" →
  // sked-professional) shifted up one tier; the legacy substring map returned
  // "standard" for sked-professional (now Accelerate) which is wrong on two
  // counts: (a) Accelerate has post-status-version=advanced, (b) "standard" is
  // reserved/unused per chargebee-plan-entitlements.json. Fallback only fires
  // when the entitlement payload is missing — server is the source of truth.
  private getFallbackPostStatusVersion = (
    plan: string | undefined
  ): "basic" | "advanced" | null => {
    if (!plan) {
      return null;
    }

    if (
      plan.includes("enterprise") ||
      plan.includes("custom") ||
      plan.includes("professional") || // post-rebrand Accelerate
      plan.includes("accelerate") ||
      plan.includes("agency") // pre-rebrand Grow alias; lifted to advanced (Accelerate-equivalent)
    ) {
      return "advanced";
    }

    if (
      plan.includes("essentials") || // covers sked-essentials and -legacy
      plan.includes("grow") ||
      plan.includes("launch") || // pre-rebrand Launch alias
      plan.includes("marketer") // pre-rebrand Launch alias
    ) {
      return "basic";
    }

    // sked-fundamentals (Basic) → null. Defensive: pre-rebrand "Launch"
    // customers were migrated to sked-essentials-legacy (caught above), but
    // the bare "launch" / "marketer" / "agency" aliases are kept on the
    // tolerant side so any user record still carrying those substrings
    // doesn't silently regress to no-approvals.
    return null;
  };

  // SKE-3599: external-approvals plan-tier fallback. Symmetric to
  // getFallbackPostStatusVersion. Accelerate / Enterprise / Custom bundle
  // external-approvals=true per chargebee-plan-entitlements.json. Only used
  // when the entitlement payload is missing/incomplete.
  private getFallbackExternalApprovals = (
    plan: string | undefined
  ): boolean => {
    if (!plan) {
      return false;
    }
    return (
      plan.includes("enterprise") ||
      plan.includes("custom") ||
      plan.includes("professional") || // post-rebrand Accelerate
      plan.includes("accelerate")
    );
  };

  hasEntitlements(entitlements: string[]): boolean {
    return entitlements.every((entitlement) =>
      Object.keys(entitlementsMappings).includes(entitlement)
    );
  }

  hasSocialListeningKeyword = (user: User) => {
    return hasEntitlement(user, "listening-keyword");
  };

  hasSocialListeningCompetitors = (user: User) => {
    return (
      Number(user.preferences?.entitlements?.["listening-competitors"]) > 0
    );
  };

  hasSocialListeningSentiment = (user: User) => {
    return hasEntitlement(user, "listening-sentiment");
  };

  hasSocialListeningAudience = (user: User) => {
    return hasEntitlement(user, "listening-audience");
  };

  hasSocialListening = (user: User) => {
    return (
      this.hasSocialListeningKeyword(user) ||
      this.hasSocialListeningAudience(user) ||
      this.hasSocialListeningSentiment(user) ||
      this.hasSocialListeningCompetitors(user)
    );
  };

  hasEditorLibrary = (user: User) => {
    return hasEntitlement(user, "editor-library");
  };

  /**
   * Strict `reports-full` check. Drives PDF export, full report-builder UI,
   * and the home-dashboard "Insights" full-data lock-state. False on Basic;
   * true on Grow / Accelerate / Enterprise / Custom (per
   * `chargebee-plan-entitlements.json`).
   *
   * NOTE: The Grow-vs-Accelerate reporting differentiator is `reports-excel`,
   * not this gate — both plans ship `reports-full:true`. See the
   * `_grow_note` in `chargebee-plan-entitlements.json`. The @insights mirror
   * lives in `react-src/@insights/utils/entitlements-permission.ts` as
   * `hasReportsFull`.
   */
  hasReportFullAccess = (user: User) => {
    return hasEntitlement(user, "reports-full");
  };

  /**
   * Gate for the **Custom Reports** entry point (home dashboard module +
   * left-nav menu lock-state). Returns `reports-full || reports-limited` —
   * i.e. "user has any reports access". NOT keyed off the `report-builder`
   * Chargebee key, which is decorative-only (see the `_decorative_keys_note`
   * in `chargebee-plan-entitlements.json`).
   *
   * Currently true on every plan because every plan ships
   * `reports-limited:true`. Kept as an OR so that flipping `reports-limited`
   * off on a plan would still leave Grow+ access via `reports-full`. The
   * @insights mirror is `hasReportBuilderPermission` /
   * `hasReportsLimited` in `react-src/@insights/utils/entitlements-permission.ts`.
   */
  hasCustomReportAccess = (user: User) => {
    return (
      this.hasReportFullAccess(user) || hasEntitlement(user, "reports-limited")
    );
  };

  getPostStatusVersion = (user: User): "basic" | "advanced" | null => {
    // chargebee-plan-entitlements.json defines `post-status-version` with
    // values null / basic / advanced. The "standard" tier is reserved/unused;
    // we don't accept it from the entitlement payload (would be a CB
    // misconfiguration) and don't return it from the fallback either.
    const entitlements = user.preferences?.entitlements ?? {};
    const hasPostStatusEntitlement = Object.prototype.hasOwnProperty.call(
      entitlements,
      "post-status-version"
    );
    const version = entitlements["post-status-version"];
    if (version === "basic" || version === "advanced") {
      return version;
    }
    if (hasPostStatusEntitlement) {
      return null;
    }
    return this.getFallbackPostStatusVersion(user.plan);
  };

  /**
   * True if the account can use external reviewer links and shared calendars.
   *
   * Driven by the `external-approvals` Chargebee switch entitlement, granted
   * by the External Approvals add-on or by the Accelerate / Custom plans.
   * NOT inferred from `post-status-version` — Internal Approvals add-on
   * customers get `post-status-version=advanced` without external review.
   *
   * DEPLOY NOTE: must ship after the server `external-approvals` backfill
   * has run for every existing paid customer who bundled external review
   * (Accelerate / Custom). Without the backfill those users temporarily
   * lose external-review access. Behaviour here is intentionally identical
   * to `hasExternalApprovals` in `utils/feature-gates.ts` — the two gate
   * the same feature from different layers (service vs. functional) and
   * must be kept in sync. See
   * `apps/schedugram-server/scripts/backfill-cache-entitlements.js`.
   */
  canAccessExternalReviewLinks = (user: User): boolean => {
    if (hasEntitlement(user, "external-approvals")) return true;
    // SKE-3599: fall back to plan tier ONLY when the entitlement key is
    // absent from the payload (e.g. cache miss / server backfill in flight).
    // If CB explicitly returned `external-approvals: false`, honour it —
    // the user's plan may bundle approvals at the post-status level but
    // not the external-link feature.
    const present =
      user.preferences?.entitlements?.["external-approvals"] !== undefined;
    if (present) return false;
    return this.getFallbackExternalApprovals(user.plan);
  };

  hasKnockNotifications = (user: User) => {
    return hasEntitlement(user, "knock-notifications");
  };

  /**
   * "Social set only" — when true, the user gets one account per platform
   * instead of a numeric quota. See utils/social-set.ts for the slot model.
   */
  hasSocialSetOnly = (user: User) => {
    return hasEntitlement(user, "social-set-only");
  };

  getMaximumBulkPosts = (user: UserWithEntitlements): number | null => {
    return parsePositiveIntEntitlement(
      user?.preferences?.entitlements?.["maximum-bulk-posts"]
    );
  };

  /**
   * Two-tier user permissions model (SKE-3397). Returns "basic" only when the
   * Chargebee `user-permissions` entitlement is explicitly "basic"; absent or
   * unrecognised values default to "advanced" so existing customers (who
   * predate the entitlement) keep today's full editing surface. The support
   * team migrates grandfathered accounts separately.
   */
  getUserPermissionsTier = (
    user: User | null | undefined
  ): UserPermissionsTier => {
    const raw = getEntitlementValue(user, "user-permissions");
    return typeof raw === "string" && raw.trim().toLowerCase() === "basic"
      ? "basic"
      : "advanced";
  };

  hasAdvancedUserPermissions = (user: User | null | undefined): boolean => {
    return this.getUserPermissionsTier(user) === "advanced";
  };
}

export const entitlementsPermission = new EntitlementsPermission();
